import { normalizeRecurringConfig } from './normalizeRecurringConfig';
import {
  DEFAULT_PAYER_OVERRIDE,
  DEFAULT_TOPS_PAYMENT_OPTIONS,
  DEFAULT_RECURRING_CHARGE_CONFIG,
  LETTER_TYPES,
  DEFAULT_TAG_OVERRIDE,
  DEFAULT_ASSURANCE_RATING,
  DEFAULT_LOW_ASSURANCE,
  DEFAULT_DOLLAR_CAP,
  PAYMENT_METHOD_FIELDS,
  PHASE_FIELDS
} from './fields';

export const paymentMethods = [
  {
    id: 1,
    field: PAYMENT_METHOD_FIELDS.CREDIT_DEBIT_CARD,
    icon: 'credit-card-filled',
    iconColor: 'primary',
    iconShade: 500,
    iconBackground: 'bg-primary-100',
    title: 'Pay today with your card',
    description: 'Pay your full estimated balance with a major credit, HSA, or FSA card.',
    allowed: ['in-person', 'remote'],
    recommended: true
  },
  {
    id: 2,
    field: PAYMENT_METHOD_FIELDS.CASH,
    icon: 'cash-filled',
    iconColor: 'moss',
    iconShade: 500,
    iconBackground: 'bg-moss-100',
    title: 'Cash',
    description: 'Let us know you’ll pay your full estimated balance in cash at your appointment.',
    allowed: ['in-person'],
    recommended: false
  },
  {
    id: 3,
    field: PAYMENT_METHOD_FIELDS.CHECK,
    icon: 'pay-by-check-filled',
    iconColor: 'neutral',
    iconShade: 700,
    title: 'Check',
    iconBackground: 'bg-neutral-100',
    description:
      'Let us know you’ll pay your full estimated balance by paper check at your appointment.',
    allowed: ['in-person'],
    recommended: false
  },
  {
    id: 4,
    field: PAYMENT_METHOD_FIELDS.ACH,
    icon: 'building-filled',
    iconColor: 'green-light',
    iconShade: 500,
    iconBackground: 'bg-green-light-100',
    title: 'Pay today with your bank account',
    description:
      'Pay your full estimated balance using a secure electronic bank payment (ACH/e-check).',
    allowed: ['in-person', 'remote'],
    recommended: false
  },
  {
    id: 5,
    field: PAYMENT_METHOD_FIELDS.PRE_AUTH,
    icon: 'shield-tick-filled',
    iconColor: 'violet',
    iconShade: 500,
    iconBackground: 'bg-violet-100',
    title: 'Guarantee any amount not covered by insurance',
    description:
      'Skip payment today and guarantee your full balance with a card. You’ll only be charged your actual patient responsibility.',
    allowed: ['in-person', 'remote'],
    recommended: false
  },
  {
    id: 6,
    field: PAYMENT_METHOD_FIELDS.RECURRING_CHARGE,
    icon: 'recurring-charges-filled',
    iconColor: 'warning',
    iconShade: 500,
    iconBackground: 'bg-warning-100',
    title: 'Request a payment plan',
    description:
      'Choose a payment schedule and securely authorize recurring charges on your card or bank account.',
    allowed: ['in-person', 'remote'],
    recommended: false
  },
  {
    id: 7,
    field: PAYMENT_METHOD_FIELDS.DEFER_PAYMENT,
    icon: 'handshake-filled',
    iconColor: 'orange-dark',
    iconShade: 500,
    iconBackground: 'bg-orange-dark-100',
    title: 'None of the above apply.',
    settingsTitle: 'Defer payment',
    description: 'Please discuss payment arrangements with our team prior to receiving treatment.',
    allowed: ['in-person', 'remote'],
    recommended: false
  }
];

const ALLOWED_CHANNELS_BY_FIELD = paymentMethods.reduce((acc, method) => {
  acc[method.field] = new Set(method.allowed);
  return acc;
}, {});

const normalizeMethodChannels = (method) => {
  const allowed = ALLOWED_CHANNELS_BY_FIELD[method.field];
  if (!allowed) return method;
  return {
    ...method,
    in_person: allowed.has('in-person') ? !!method.in_person : false,
    remote: allowed.has('remote') ? !!method.remote : false
  };
};

export const DEFAULT_PAYMENT_SETUP = {
  letter_type: LETTER_TYPES.GOOD_FAITH_ESTIMATE,
  tops_payment_options: DEFAULT_TOPS_PAYMENT_OPTIONS,
  assurance_thresholds: {
    show_patient_rating: DEFAULT_ASSURANCE_RATING,
    show_on_letter: true
  },
  low_assurance: DEFAULT_LOW_ASSURANCE,
  payment_methods: [
    { field: PAYMENT_METHOD_FIELDS.CASH, in_person: true, remote: false },
    { field: PAYMENT_METHOD_FIELDS.CHECK, in_person: true, remote: false },
    { field: PAYMENT_METHOD_FIELDS.CREDIT_DEBIT_CARD, in_person: true, remote: true },
    { field: PAYMENT_METHOD_FIELDS.ACH, in_person: true, remote: true },
    { field: PAYMENT_METHOD_FIELDS.PRE_AUTH, in_person: true, remote: true },
    {
      field: PAYMENT_METHOD_FIELDS.RECURRING_CHARGE,
      in_person: true,
      remote: true,
      config: DEFAULT_RECURRING_CHARGE_CONFIG
    },
    { field: PAYMENT_METHOD_FIELDS.DEFER_PAYMENT, in_person: true, remote: true }
  ],
  phases: [
    {
      field: PHASE_FIELDS.IN_NETWORK,
      card_on_file: { route_to: 'patient-option' },
      authorization_applied: { route_to: 'all-visits-this-year' },
      charge_limit: {
        route_to: 'combined-allowable-amount',
        config: { dollar_cap: DEFAULT_DOLLAR_CAP }
      }
    },
    {
      field: PHASE_FIELDS.OUT_OF_NETWORK,
      card_on_file: { route_to: 'patient-option' },
      authorization_applied: { route_to: 'all-visits-this-year' },
      billing_method: { route_to: 'eligibility-calculation', config: {} },
      patient_portion_cpt: { route_to: 'self-pay-amount' },
      balance_billing: { enabled: false }
    },
    {
      field: PHASE_FIELDS.SELF_PAY,
      card_on_file: { route_to: 'patient-option' },
      authorization_applied: { route_to: 'all-visits-this-year' },
      charge_limit: { route_to: 'guarantee-full-cost', config: { dollar_cap: DEFAULT_DOLLAR_CAP } }
    }
  ],
  payer_override: DEFAULT_PAYER_OVERRIDE,
  tag_override: DEFAULT_TAG_OVERRIDE
};

export const hydratePaymentSetup = (incoming) => {
  const base = DEFAULT_PAYMENT_SETUP;
  const mergeTopsOptions = (incomingOptions) =>
    Object.entries(base.tops_payment_options).reduce((acc, [field, defaults]) => {
      acc[field] = { ...defaults, ...(incomingOptions?.[field] ?? {}) };
      return acc;
    }, {});

  if (!incoming) {
    return {
      ...base,
      tops_payment_options: mergeTopsOptions(),
      payment_methods: base.payment_methods.map((method) =>
        normalizeRecurringConfig(normalizeMethodChannels(method))
      )
    };
  }

  const incomingMethods = incoming.payment_methods;
  const incomingByField = new Map(
    Array.isArray(incomingMethods) ? incomingMethods.map((m) => [m.field, m]) : []
  );
  const mergedMethods = [
    ...base.payment_methods.map((defaultMethod) => {
      const incomingMethod = incomingByField.get(defaultMethod.field);
      incomingByField.delete(defaultMethod.field);
      return normalizeRecurringConfig(
        normalizeMethodChannels({
          ...defaultMethod,
          ...(incomingMethod ?? {}),
          ...(defaultMethod.config && {
            config: { ...defaultMethod.config, ...(incomingMethod?.config ?? {}) }
          })
        })
      );
    }),
    ...[...incomingByField.values()].map(normalizeMethodChannels)
  ];

  const incomingPhaseByField = new Map((incoming.phases ?? []).map((p) => [p.field, p]));
  const mergedPhases = base.phases.map((defaultPhase) => ({
    ...defaultPhase,
    ...(incomingPhaseByField.get(defaultPhase.field) ?? {})
  }));

  return {
    ...base,
    ...incoming,
    assurance_thresholds: {
      ...base.assurance_thresholds,
      ...(incoming.assurance_thresholds ?? {})
    },
    low_assurance: { ...base.low_assurance, ...(incoming.low_assurance ?? {}) },
    tops_payment_options: mergeTopsOptions(incoming.tops_payment_options),
    payment_methods: mergedMethods,
    phases: mergedPhases,
    payer_override: { ...base.payer_override, ...(incoming.payer_override ?? {}) },
    tag_override: { ...base.tag_override, ...(incoming.tag_override ?? {}) }
  };
};
